import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Volume2, Loader2, BookMarked, Quote } from "lucide-react";
import AppShell from "@/components/AppShell";
import client from "@/lib/api";
import { useTts } from "@/hooks/useTts";

const LANG = () => (localStorage.getItem("melletic_lang") || "en").startsWith("de") ? "de" : "en";
const LB = {
  en: { title: "My Vocabulary", words: "Words", phrases: "Phrases", all: "All", new: "New",
        learning: "Learning", strong: "Strong", review: "Review due", empty: "Complete lessons to collect vocabulary.",
        topic: "Topic" },
  de: { title: "Mein Wortschatz", words: "Wörter", phrases: "Redewendungen", all: "Alle", new: "Neu",
        learning: "Am Lernen", strong: "Sicher", review: "Wiederholen", empty: "Schließe Lektionen ab, um Vokabeln zu sammeln.",
        topic: "Thema" },
};
const BADGE = {
  new: "bg-slate-100 text-slate-500", learning: "bg-sky-100 text-sky-700",
  strong: "bg-emerald-100 text-emerald-700", review: "bg-amber-100 text-amber-700",
};
const STATES = ["all", "new", "learning", "strong", "review"];
const normStatus = (s) => (s === "learned" ? "strong" : (s || "new"));

export default function MyWords() {
  const nav = useNavigate();
  const { play, playing } = useTts();
  const L = LB[LANG()];
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  const [tab, setTab] = useState("words");   // words | phrases
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    client.get("/learn/words").then(({ data }) => setRows(data)).catch(() => setRows([]));
    client.get("/learn/words/counts").then(({ data }) => setCounts(data)).catch(() => {});
  }, []);

  const shown = useMemo(() => {
    return (rows || []).filter((w) => {
      const k = w.kind || "word";
      const isPhrase = k === "phrase" || k === "sentence";
      if (tab === "words" && isPhrase) return false;
      if (tab === "phrases" && !isPhrase) return false;
      if (filter !== "all" && normStatus(w.status) !== filter) return false;
      return true;
    });
  }, [rows, tab, filter]);

  const stateCount = (st) => (rows || []).filter((w) => {
    const isPhrase = (w.kind || "word") !== "word";
    if (tab === "words" && isPhrase) return false;
    if (tab === "phrases" && !isPhrase) return false;
    return st === "all" ? true : normStatus(w.status) === st;
  }).length;

  return (
    <AppShell>
      <div className="pt-10 pb-2 flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="words-back" className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center"><ArrowLeft size={20} className="text-slate-600" /></button>
        <div><h1 className="font-heading text-2xl font-extrabold tracking-tight text-[#0F172A]">{L.title}</h1>
          <p className="text-slate-500 text-sm">{counts ? `${counts.words} ${L.words} · ${counts.phrases} ${L.phrases}` : ""}</p></div>
      </div>

      {/* Words / Phrases tabs */}
      <div className="mt-1 grid grid-cols-2 gap-2" data-testid="words-tabs">
        {["words", "phrases"].map((tb) => (
          <button key={tb} onClick={() => { setTab(tb); setFilter("all"); }} data-testid={`words-tab-${tb}`}
            className={`py-2 rounded-2xl text-sm font-bold border ${tab === tb ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-slate-600 border-slate-200"}`}>
            {L[tb]} {counts ? `(${counts[tb]})` : ""}
          </button>
        ))}
      </div>

      {/* State filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 mt-3 -mx-1 px-1" data-testid="words-filters">
        {STATES.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`words-filter-${f}`}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold ${filter === f ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            {L[f]} {stateCount(f) ? `(${stateCount(f)})` : ""}
          </button>
        ))}
      </div>

      {!rows ? <div className="pt-20 flex justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div> : (
        shown.length === 0 ? (
          <div className="pt-16 text-center text-slate-400"><BookMarked className="mx-auto mb-3" size={40} />{L.empty}</div>
        ) : (
          <div className="mt-3 space-y-2">
            {shown.map((w, i) => {
              const st = normStatus(w.status);
              return (
                <div key={i} data-testid={`word-${i}`} className="flex items-start gap-3 bg-white border border-slate-200 rounded-2xl p-3">
                  {w.image ? <img src={w.image} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />
                    : <span className="w-12 h-12 rounded-xl bg-[#E0F2FE] flex items-center justify-center text-[#2563EB] shrink-0">{tab === "phrases" ? <Quote size={18} /> : <span className="font-bold">{(w.word || "?")[0]}</span>}</span>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-[#0F172A] truncate">{w.word}</p>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${BADGE[st]}`}>{L[st]}</span>
                    </div>
                    {w.forms && <p className="text-[11px] text-slate-400 italic truncate">{w.forms}</p>}
                    <p className="text-sm text-slate-500 truncate">{w.translation}</p>
                    {w.example && w.example !== w.word && <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">“{w.example}”</p>}
                    <div className="flex items-center gap-1.5 mt-1">
                      {w.cefr && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{w.cefr}</span>}
                      {w.topic && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-500 truncate max-w-[55%]">{w.topic}</span>}
                    </div>
                  </div>
                  <button onClick={() => play(w.audio_text || w.word, w.language, w.word)} data-testid={`word-audio-${i}`}
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${playing === w.word ? "bg-[#2563EB] text-white" : "bg-[#2563EB]/10 text-[#2563EB]"}`}><Volume2 size={18} /></button>
                </div>
              );
            })}
          </div>
        )
      )}
    </AppShell>
  );
}
